// Main App component with routing
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MeshCanvas } from './components/MeshCanvas';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { CommunityResourcesPage } from './pages/CommunityResourcesPage';
import { MeshToolsPage } from './pages/MeshToolsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import type { MeshControls } from './types';
import { buildKNN, toNormX, toNormY, toPxX, toPxY, clamp } from './utils/meshUtils';
import { INNER_MARGIN_N } from './constants';
import { WelcomeModal } from './components/WelcomeModal';
import { PrivacyPage } from './pages/PrivacyPage';

function AppContent() {
  const [controls, setControls] = useState<MeshControls>({
    nodeCount: 84,
    neighborK: 3,
    traffic: 6,
    lineOpacity: 0.35,
    pulseColor: '#22d3ee', // cyan-400
    topologyMode: 'radius', // Radius (realistic) by default for better accuracy
    maxRange: 0.15, // 15% of canvas (~7.5mi/12km) - realistic LoRa range for suburban
    distanceUnit: 'mi', // Default to miles
    playgroundMode: false, // Start with full content visible
    playgroundInteractionMode: 'pan', // Default to pan in playground mode
    zoomLevel: 1.0, // Default zoom (100%)
    paused: false, // Start with simulation running
  });
  const [regenerateTrigger, setRegenerateTrigger] = useState(0);
  const [showContent, setShowContent] = useState(true); // Track when to show content
  const prevPlaygroundModeRef = useRef(controls.playgroundMode);

  const handleControlsChange = (updates: Partial<MeshControls>) => {
    setControls((prev) => ({ ...prev, ...updates }));
  };

  const handleRegenerate = () => {
    setRegenerateTrigger((prev) => prev + 1);
  };

  // Handle content visibility during mode transitions
  useEffect(() => {
    if (prevPlaygroundModeRef.current !== controls.playgroundMode) {
      if (controls.playgroundMode) {
        // Switching to playground: hide content immediately
        setShowContent(false);
      } else {
        // Switching to normal: delay showing content until after mesh deconstructs
        setShowContent(false);
        const timer = setTimeout(() => {
          setShowContent(true);
        }, 1000); // Wait for deconstruction (0.8s) + small buffer before fading in content
        return () => clearTimeout(timer);
      }
      prevPlaygroundModeRef.current = controls.playgroundMode;
    }
  }, [controls.playgroundMode]);

  // Dev-time sanity checks (run once on mount)
  useEffect(() => {
    function assert(name: string, cond: boolean) {
      if (!cond) console.error(`[mesh-test] FAIL: ${name}`);
      else console.log(`[mesh-test] PASS: ${name}`);
    }

    // 1) buildKNN returns no duplicates and valid indices
    const edges = buildKNN(
      [
        { x: 0.1, y: 0.1 },
        { x: 0.9, y: 0.9 },
        { x: 0.1, y: 0.9 },
      ],
      3
    );
    const set = new Set(edges.map((e) => `${e.a}-${e.b}`));
    assert('unique edges', set.size === edges.length);
    assert(
      'edge indices valid',
      edges.every((e) => e.a >= 0 && e.b >= 0 && Number.isFinite(e.len))
    );

    // 2) Mapping round trip stays inside [0,1]
    const W = 800,
      H = 600;
    const nx = toNormX(toPxX(0.42, W), W);
    const ny = toNormY(toPxY(0.77, H), H);
    assert('round-trip mapping', Math.abs(nx - 0.42) < 1e-6 && Math.abs(ny - 0.77) < 1e-6);

    // 3) Packets refuse to spawn when there are no edges
    const noEdges = buildKNN([{ x: 0.2, y: 0.2 }], 3);
    assert('no edges when <2 nodes', noEdges.length === 0);

    // 4) Clicks clamp inside inner margin
    const testClampX = clamp(-0.2, INNER_MARGIN_N, 1 - INNER_MARGIN_N);
    const testClampY = clamp(1.2, INNER_MARGIN_N, 1 - INNER_MARGIN_N);
    assert(
      'clamp keeps inside inner margin',
      testClampX >= INNER_MARGIN_N && testClampY <= 1 - INNER_MARGIN_N
    );
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ minHeight: '100vh', background: '#0a0a0a', color: '#eaeaea' }}
    >
      {/* Interactive Mesh Canvas Background */}
      <MeshCanvas controls={controls} onControlsChange={handleControlsChange} regenerateTrigger={regenerateTrigger}>
        {(mesh, activePackets) => (
          <>
            {/* Sticky Header with Integrated Controls */}
            <Header 
              controls={controls} 
              onControlsChange={handleControlsChange} 
              onRegenerate={handleRegenerate}
              mesh={mesh}
              activePackets={activePackets}
            />
            {/* Small welcome popup after short delay */}
            <WelcomeModal />
          </>
        )}
      </MeshCanvas>

      {/* Page Content - Add padding-top to account for sticky header */}
      {!controls.playgroundMode && showContent && (
      <div 
        className="pt-16 transition-opacity duration-1000 pointer-events-none"
        style={{
          animation: 'fadeIn 1000ms ease-out forwards'
        }}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/community-resources" element={<CommunityResourcesPage />} />
          <Route path="/mesh-tools" element={<MeshToolsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
      </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}